'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram, StakeholderView } from '@/lib/archimate/extended-types';
import {
  createStakeholderView,
  generateExecutiveSummary,
  generateStakeholderPresentation,
  exportStakeholderView,
} from '@/lib/archimate/stakeholder-utils';
import { Users, Plus, X, Eye, Download, Presentation, FileText } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface StakeholderPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onSelectElement: (elementId: string) => void;
}

export default function StakeholderPanel({
  diagram,
  onUpdate,
  onClose,
  onSelectElement,
}: StakeholderPanelProps) {
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'view'>('list');
  const [selectedView, setSelectedView] = useState<StakeholderView | null>(null);
  const [newViewForm, setNewViewForm] = useState({
    name: '',
    audience: 'executive' as 'executive' | 'technical' | 'business' | 'mixed',
    viewLevel: 'strategic' as 'strategic' | 'business' | 'technical',
  });
  const [showHelp, setShowHelp] = useState(false);

  const handleCreateView = () => {
    if (!newViewForm.name.trim()) return;

    const newView = createStakeholderView(
      newViewForm.name,
      newViewForm.audience,
      newViewForm.viewLevel
    );

    const updatedDiagram = {
      ...diagram,
      stakeholderViews: [...diagram.stakeholderViews, newView],
    };

    onUpdate(updatedDiagram);
    setNewViewForm({ name: '', audience: 'executive', viewLevel: 'strategic' });
    setViewMode('list');
  };

  const handleDeleteView = (viewId: string) => {
    const updatedDiagram = {
      ...diagram,
      stakeholderViews: diagram.stakeholderViews.filter((v) => v.id !== viewId),
    };

    onUpdate(updatedDiagram);
    if (selectedView?.id === viewId) {
      setSelectedView(null);
    }
  };

  const handleExportView = (view: StakeholderView) => {
    // Generate markdown report for this view
    let markdown = `# ${view.name}\n\n`;
    markdown += `**Audience:** ${view.audience}\n`;
    markdown += `**View Level:** ${view.viewLevel}\n\n`;
    markdown += `${view.description}\n\n`;
    markdown += `## Visible Layers\n\n`;
    view.visibleLayers.forEach((layer) => {
      markdown += `- ${layer}\n`;
    });
    markdown += `\n## Simplifications\n\n`;
    if (view.simplifications.hideRelationshipLabels) markdown += `- Hide relationship labels\n`;
    if (view.simplifications.hideEmptyProperties) markdown += `- Hide empty properties\n`;
    if (view.simplifications.groupByCapability) markdown += `- Group by capability\n`;

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${view.name.replace(/\s+/g, '-')}-stakeholder-view.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGeneratePresentation = (view: StakeholderView) => {
    const slides = generateStakeholderPresentation(diagram);
    const presentation = exportStakeholderView(diagram, slides);
    const blob = new Blob([presentation], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${view.name.replace(/\s+/g, '-')}-presentation.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerateExecutiveSummary = () => {
    const summary = generateExecutiveSummary(diagram);
    const blob = new Blob([summary], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-executive-summary.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getAudienceColor = (audience: string) => {
    switch (audience) {
      case 'executive':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/20';
      case 'technical':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/20';
      case 'business':
        return 'bg-green-500/20 text-green-400 border-green-500/20';
      case 'mixed':
        return 'bg-accent-muted text-accent border-border';
      default:
        return 'bg-cream/10 text-foreground-secondary border-border';
    }
  };

  const renderList = () => {
    return (
      <div className="space-y-4">
        {/* Quick Actions */}
        <div className="space-y-2">
          <button
            onClick={() => setViewMode('create')}
            className="w-full px-4 py-3 bg-accent-muted hover:bg-accent-muted border border-border text-accent rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Stakeholder View
          </button>

          <button
            onClick={handleGenerateExecutiveSummary}
            className="w-full px-4 py-3 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 text-purple-400 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Generate Executive Summary
          </button>
        </div>

        {/* Info Card */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <div className="text-sm text-foreground-secondary mb-2">
            <span className="font-medium text-foreground">Stakeholder Views</span> help you communicate
            architecture to different audiences
          </div>
          <div className="text-xs text-foreground-tertiary space-y-1">
            <div>• Executive: High-level strategy and ROI</div>
            <div>• Technical: Implementation details</div>
            <div>• Business: Process and capabilities</div>
            <div>• Audit: Compliance and security</div>
          </div>
        </div>

        {/* Views List */}
        {diagram.stakeholderViews.length === 0 ? (
          <div className="p-8 text-center text-foreground-tertiary text-sm">
            No stakeholder views defined yet. Create a view to communicate architecture to specific
            audiences.
          </div>
        ) : (
          <div className="space-y-3">
            {diagram.stakeholderViews.map((view) => (
              <div
                key={view.id}
                className="p-4 bg-cream/5 border border-border rounded-lg hover:bg-cream/10 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-foreground">{view.name}</span>
                      <span
                        className={`px-2 py-0.5 text-xs rounded capitalize ${getAudienceColor(
                          view.audience
                        )}`}
                      >
                        {view.audience}
                      </span>
                    </div>
                    {view.description && (
                      <div className="text-sm text-foreground-secondary mb-2">{view.description}</div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-foreground-tertiary">
                      <span>{view.visibleLayers.length} visible layers</span>
                      <span>•</span>
                      <span>{view.hiddenElementTypes.length} hidden types</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedView(view);
                      setViewMode('view');
                    }}
                    className="flex-1 px-3 py-1.5 text-xs bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View
                  </button>
                  <button
                    onClick={() => handleGeneratePresentation(view)}
                    className="flex-1 px-3 py-1.5 text-xs bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Presentation className="w-3.5 h-3.5" />
                    Presentation
                  </button>
                  <button
                    onClick={() => handleExportView(view)}
                    className="px-3 py-1.5 text-xs bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteView(view.id)}
                    className="px-3 py-1.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCreate = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-cream/5 border border-border rounded-lg space-y-4">
          <h3 className="text-sm font-medium text-foreground">Create Stakeholder View</h3>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">View Name</label>
            <input
              type="text"
              value={newViewForm.name}
              onChange={(e) => setNewViewForm({ ...newViewForm, name: e.target.value })}
              placeholder="e.g., Q4 Board Presentation, Security Audit View"
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">Target Audience</label>
            <select
              value={newViewForm.audience}
              onChange={(e) =>
                setNewViewForm({
                  ...newViewForm,
                  audience: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            >
              <option value="executive">Executive (C-level, Board)</option>
              <option value="technical">Technical (Engineers, Architects)</option>
              <option value="business">Business (Product, Operations)</option>
              <option value="mixed">Mixed (Multiple Stakeholders)</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">View Level</label>
            <select
              value={newViewForm.viewLevel}
              onChange={(e) =>
                setNewViewForm({
                  ...newViewForm,
                  viewLevel: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            >
              <option value="strategic">Strategic (High-level, Goals)</option>
              <option value="business">Business (Processes, Capabilities)</option>
              <option value="technical">Technical (Architecture, Systems)</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCreateView}
              disabled={!newViewForm.name.trim()}
              className="flex-1 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create View
            </button>
            <button
              onClick={() => {
                setViewMode('list');
                setNewViewForm({ name: '', audience: 'executive', viewLevel: 'strategic' });
              }}
              className="px-4 py-2 bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Audience Guide */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Audience Focus</h4>
          <div className="space-y-2">
            <div className={`p-3 rounded-lg border ${getAudienceColor('executive')}`}>
              <div className="font-medium text-sm mb-1">Executive</div>
              <div className="text-xs opacity-80">
                Business value, ROI, strategic alignment, risks
              </div>
            </div>
            <div className={`p-3 rounded-lg border ${getAudienceColor('technical')}`}>
              <div className="font-medium text-sm mb-1">Technical</div>
              <div className="text-xs opacity-80">
                Architecture patterns, tech stack, scalability, performance
              </div>
            </div>
            <div className={`p-3 rounded-lg border ${getAudienceColor('business')}`}>
              <div className="font-medium text-sm mb-1">Business</div>
              <div className="text-xs opacity-80">
                Capabilities, processes, dependencies, timelines
              </div>
            </div>
            <div className={`p-3 rounded-lg border ${getAudienceColor('mixed')}`}>
              <div className="font-medium text-sm mb-1">Mixed</div>
              <div className="text-xs opacity-80">
                Combined view for multiple stakeholders with different concerns
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderView = () => {
    if (!selectedView) return null;

    return (
      <div className="space-y-4">
        {/* View Header */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-foreground mb-2">{selectedView.name}</h3>
              <span
                className={`inline-block px-2 py-1 text-xs rounded capitalize ${getAudienceColor(
                  selectedView.audience
                )}`}
              >
                {selectedView.audience}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleGeneratePresentation(selectedView)}
                className="p-2 hover:bg-cream/10 rounded transition-colors"
                title="Generate Presentation"
              >
                <Presentation className="w-4 h-4 text-foreground" />
              </button>
              <button
                onClick={() => handleExportView(selectedView)}
                className="p-2 hover:bg-cream/10 rounded transition-colors"
                title="Export View"
              >
                <Download className="w-4 h-4 text-foreground" />
              </button>
              <button
                onClick={() => handleDeleteView(selectedView.id)}
                className="p-2 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                title="Delete View"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {selectedView.description && (
            <div className="text-sm text-foreground-secondary">{selectedView.description}</div>
          )}
        </div>

        {/* Visible Layers */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">
            Visible Layers ({selectedView.visibleLayers.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {selectedView.visibleLayers.map((layer) => (
              <div key={layer} className="px-3 py-1 bg-green-500/10 border border-green-500/20 text-green-400 rounded text-sm">
                {layer}
              </div>
            ))}
          </div>
        </div>

        {/* Hidden Element Types */}
        {selectedView.hiddenElementTypes.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Hidden Element Types ({selectedView.hiddenElementTypes.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {selectedView.hiddenElementTypes.map((type) => (
                <div key={type} className="px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 rounded text-sm">
                  {type.replace(/-/g, ' ')}
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Simplifications */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">View Simplifications</h4>
          <div className="p-3 bg-cream/5 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${selectedView.simplifications.hideRelationshipLabels ? 'bg-green-400' : 'bg-cream/20'}`}></div>
              <span className="text-foreground-secondary">Hide relationship labels</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${selectedView.simplifications.hideEmptyProperties ? 'bg-green-400' : 'bg-cream/20'}`}></div>
              <span className="text-foreground-secondary">Hide empty properties</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${selectedView.simplifications.groupByCapability ? 'bg-green-400' : 'bg-cream/20'}`}></div>
              <span className="text-foreground-secondary">Group by capability</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Stakeholder Views</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="stakeholder"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <button
          onClick={() => setViewMode('list')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'list' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Views
        </button>
        {selectedView && (
          <button
            onClick={() => setViewMode('view')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'view' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Details
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {viewMode === 'list' && renderList()}
        {viewMode === 'create' && renderCreate()}
        {viewMode === 'view' && renderView()}
      </div>
    </div>
  );
}
